export const CONTENT_BLOCKS_GUIDE = {
  id: "content-blocks",
  title: "Content Blocks",
  description:
    "How to edit and publish website content blocks — the modular sections that make up your public website pages.",
  icon: "Blocks",
  version: "1.0.0",
  lastUpdated: "2026-03-13",
  requires: [],
  sections: [
    /* ---------------------------------------------------------- */
    /*  Overview                                                   */
    /* ---------------------------------------------------------- */
    {
      id: "overview",
      title: "Overview",
      content: [
        {
          type: "paragraph",
          text: "**Content Blocks** are the editable sections of your public website. Each page on your site is made up of one or more blocks — for example, the Home page might have a Hero section, a Thesis section, and a Call to Action block.",
        },
        {
          type: "paragraph",
          text: "The Content Blocks page in the CRM lets you edit any of these sections using a visual drag-and-drop editor, save drafts, and publish changes live to your website — all without touching code or needing access to a separate CMS.",
        },
        {
          type: "paragraph",
          text: "Key capabilities:",
        },
        {
          type: "steps",
          items: [
            "**Visual editor** — drag-and-drop page builder (GrapesJS) with text, images, layout components, and custom HTML",
            "**Block registry** — all editable blocks are pre-defined and grouped by page, so you always know what you're editing",
            "**Draft & Publish workflow** — save drafts without affecting the live site, then publish when ready",
            "**Static bake-in** — publishing pushes your changes directly into the website HTML files for fast loading",
            "**Fallback loading** — if a block hasn't been edited yet, the website shows its default content",
          ],
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Finding Your Blocks                                        */
    /* ---------------------------------------------------------- */
    {
      id: "finding-blocks",
      title: "Finding Your Blocks",
      content: [
        {
          type: "steps",
          items: [
            "Go to **Blocks** from the sidebar",
            "You'll see all available blocks **grouped by page** (e.g. Home, Services, Blog, Videos)",
            "Each block card shows its **label**, **block ID**, and **status** (Published, Draft, or Not Created)",
            "The stats bar at the top shows totals: how many blocks are published, in draft, or not yet created",
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "The list of available blocks is defined per tenant. FGF and L30 each have their own set of blocks matching their website structure.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Editing a Block                                            */
    /* ---------------------------------------------------------- */
    {
      id: "editing",
      title: "Editing a Block",
      content: [
        {
          type: "steps",
          items: [
            "Click **Edit** on any block card to open the visual editor",
            "The editor loads with the block's current content (or blank if it's a new block)",
            "Use the **component panels** on the right to drag in text, images, columns, links, buttons, and other elements",
            "Click any element on the canvas to select it — then use the **style panel** to adjust colours, spacing, fonts, and layout",
            "Use the **layers panel** to see the element hierarchy and select nested items",
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "The editor saves the block's HTML and CSS separately. This means your styles won't conflict with other blocks or the page's base styles.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Working with the Editor                                    */
    /* ---------------------------------------------------------- */
    {
      id: "editor-tips",
      title: "Working with the Editor",
      content: [
        {
          type: "paragraph",
          text: "The GrapesJS editor has several panels and tools:",
        },
        {
          type: "table",
          headers: ["Panel", "What it does"],
          rows: [
            [
              "**Blocks (grid icon)**",
              "Drag pre-built components onto the canvas — text, images, columns, links, videos, maps, and more",
            ],
            [
              "**Layers (stack icon)**",
              "View the element tree. Useful for selecting elements that are hard to click on the canvas",
            ],
            [
              "**Style (paintbrush icon)**",
              "Edit CSS properties of the selected element — colours, margins, padding, borders, typography, flexbox/grid layout",
            ],
            [
              "**Settings (cog icon)**",
              "Edit element attributes like class names, IDs, href for links, src for images",
            ],
            [
              "**Code view (</>)**",
              "Switch to raw HTML/CSS editing for advanced users",
            ],
          ],
        },
        {
          type: "callout",
          variant: "tip",
          text: "Double-click any text element to edit it inline. Press Escape to deselect.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Saving a Draft                                             */
    /* ---------------------------------------------------------- */
    {
      id: "saving",
      title: "Saving a Draft",
      content: [
        {
          type: "steps",
          items: [
            "After making changes, click **Save Draft** at the bottom of the editor",
            "The block status changes to **Draft** — your changes are saved in the CRM but **not yet visible** on the live website",
            "You can close the editor and come back later — your draft will be preserved",
            "Click **Back** to return to the block list",
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "Drafts are stored in the CRM database. The live website continues to show the previously published version (or the default content if the block was never published).",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Publishing                                                 */
    /* ---------------------------------------------------------- */
    {
      id: "publishing",
      title: "Publishing a Block",
      content: [
        {
          type: "steps",
          items: [
            "When you're happy with your changes, click **Publish** at the bottom of the editor",
            "This does two things: saves the block as **Published** in the database, and triggers a **static publish** to bake the content into the website HTML files",
            "The static publish pushes the updated HTML to GitHub, which triggers the website's deployment pipeline",
            "Within a minute or two, your changes will be live on the public website",
          ],
        },
        {
          type: "callout",
          variant: "warning",
          text: "Publishing is immediate — there is no approval step. Make sure you're happy with your content before clicking Publish.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  How It Works Behind the Scenes                             */
    /* ---------------------------------------------------------- */
    {
      id: "how-it-works",
      title: "How It Works Behind the Scenes",
      content: [
        {
          type: "paragraph",
          text: "Understanding the publish flow helps you troubleshoot issues:",
        },
        {
          type: "steps",
          items: [
            "Each website page has HTML elements marked with **data-cms-block** attributes (e.g. `data-cms-block=\"l30-hero\"`)",
            "When you publish, the **static publish** process fetches all published blocks from the database",
            "It scans each HTML page file for `data-cms-block` markers and replaces the inner content with the CMS version",
            "The updated HTML files are pushed to the website's GitHub repository",
            "Azure Static Web Apps automatically deploys the changes",
          ],
        },
        {
          type: "paragraph",
          text: "There's also a **client-side fallback**: each page includes a small script that fetches block content from `/api/block-content/{blockId}` on load. This means even if the static publish hasn't run yet, visitors will see the latest published content (loaded via API with a 5-minute cache).",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Block Reference — L30                                      */
    /* ---------------------------------------------------------- */
    {
      id: "l30-blocks",
      title: "L30 Website Blocks",
      content: [
        {
          type: "paragraph",
          text: "The L30 website (Latitude30 Advisors) has **12 editable blocks** across 7 pages:",
        },
        {
          type: "table",
          headers: ["Page", "Block ID", "What it controls"],
          rows: [
            ["Home", "`l30-hero`", "Hero banner with headline, subtitle, and CTA button"],
            ["Home", "`l30-thesis`", "The Latitude30 Thesis — explanatory section below the hero"],
            ["Home", "`l30-expertise`", "Core Expertise cards (3 service cards)"],
            ["Home", "`l30-cta`", "Call to Action section at the bottom"],
            ["The L30 Zone", "`l30-zone-intro`", "Zone introduction with map image and description"],
            ["The L30 Zone", "`l30-zone-stats`", "Key statistics cards (80%, 70%, 90%)"],
            ["The L30 Zone", "`l30-zone-mission`", "Mission statement banner"],
            ["Services", "`l30-services`", "Full services page content"],
            ["Sectors", "`l30-sectors`", "Sector expertise cards and descriptions"],
            ["Contact", "`l30-contact-info`", "Contact details sidebar (address, email, phone)"],
            ["Blog", "`l30-blog`", "Blog article cards grid"],
            ["Videos", "`l30-video-gallery`", "Video gallery cards with YouTube thumbnails"],
          ],
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Block Reference — FGF                                      */
    /* ---------------------------------------------------------- */
    {
      id: "fgf-blocks",
      title: "FGF Website Blocks",
      content: [
        {
          type: "paragraph",
          text: "The FGF website (Fordham Global Foresight) has **10 editable blocks** across 5 pages:",
        },
        {
          type: "table",
          headers: ["Page", "Block ID", "What it controls"],
          rows: [
            ["Home", "`fgf-hero`", "Hero banner section"],
            ["Home", "`fgf-announcement`", "Announcement bar at the top of the page"],
            ["Home", "`fgf-pitch`", "\"Boards That Are Going Places\" pitch section"],
            ["Home", "`fgf-insights-grid`", "Latest Insights video grid on the homepage"],
            ["About", "`fgf-about-intro`", "About page introduction text"],
            ["About", "`fgf-team`", "Team member profile cards"],
            ["Services", "`fgf-services`", "Services overview content"],
            ["Insights", "`fgf-insights`", "Insights & Influence page content"],
            ["Insights", "`fgf-video-gallery`", "Video gallery grid"],
            ["Newsletter", "`fgf-newsletter`", "Newsletter signup section"],
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "The FGF website is currently hosted on WordPress. These blocks are ready for when the site migrates to static HTML. In the meantime, you can use the block editor to prepare content.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  FAQ                                                        */
    /* ---------------------------------------------------------- */
    {
      id: "faq",
      title: "Frequently Asked Questions",
      content: [
        {
          type: "faq",
          question: "What happens if I haven't edited a block yet?",
          answer:
            "The website shows its default content — the HTML that was originally built into the page. The block only gets replaced when you publish content for it from the CRM.",
        },
        {
          type: "faq",
          question: "Can I revert a block to its original content?",
          answer:
            "Yes — delete the block from the CRM (by removing its saved content). The website will fall back to showing the default HTML that's built into the page template.",
        },
        {
          type: "faq",
          question: "Will editing a block affect other pages?",
          answer:
            "No. Each block is independent and only affects the specific section it's mapped to. Editing the Hero block on the Home page won't affect the Services page.",
        },
        {
          type: "faq",
          question: "How long does it take for published changes to go live?",
          answer:
            "Usually 1–2 minutes. The static publish pushes to GitHub, which triggers an Azure Static Web Apps deployment. The client-side fallback script also fetches published blocks via API, so visitors may see updates even before the static deploy completes.",
        },
        {
          type: "faq",
          question: "Who can edit content blocks?",
          answer:
            "Users with the **Exec** or **Admin** role can access the Blocks page and edit content.",
        },
      ],
    },
  ],
};
